
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const HomeEn: React.FC = () => {
  const { t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 flex items-center justify-center gap-2 md:h-20 bg-[#071C74] px-4 py-4 font-medium md:flex-none md:justify-start md:p-2 md:px-3">
        <div className="sticky top-0 flex h-7 items-center justify-between md:h-20 w-full max-w-7xl mx-auto">
          <div className="adi-header-left">
            <a href="/">
              <img 
                alt="ADI Logo" 
                width={125} 
                height={56} 
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/3900b388860a5efa532a02080a385ec7.png" 
                className="h-14 w-auto"
              />
            </a>
          </div>
          
          <div className="hidden md:flex items-center space-x-4 text-white">
            <div className="relative">
              <div className="flex bg-white/10 backdrop-blur-sm rounded-full p-1">
                <a 
                  href="/"
                  className="px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                >
                  简体中文
                </a>
                <button 
                  className="px-4 py-2 text-white bg-white/20 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                  onClick={() => {
                    setSelectedLanguage('English');
                    setIsLanguageDropdownOpen(false);
                  }}
                >
                  English
                </button>
              </div>
            </div>
            <a 
              href="/flanexus/en" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-4 py-2 text-white hover:text-blue-200 transition-colors"
            >
              Flanexus
            </a>
            
            <a 
              href="/en/news"
              className="px-4 py-2 text-white hover:text-blue-200 transition-colors"
            >
              News
            </a>
            
            <a 
              href="/en/contact-us"
              className="px-4 py-2 border border-white rounded-full hover:bg-white hover:text-[#071C74] transition-colors text-white whitespace-nowrap"
            >
              Contact Us
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="h-[920px] md:h-[880px] bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url("https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/3138850d221a5ab660120f205947f752.jpeg")`
        }}
      >
        <div className="max-w-7xl mx-auto h-full w-full px-4 relative z-10">
          <div className="flex w-full items-center h-full">
            <div className="flex max-w-[100%] flex-col justify-start text-left lg:max-w-[550px]">
              <h1 className="font-bold text-white text-[40px] leading-[60px] md:text-[60px] md:leading-[80px] lg:text-[80px] xl:text-[90px] xl:leading-[110px] mb-6">
                Asia Digital<br />Institute
              </h1>
              <p className="text-left text-sm text-white/85 md:text-[15px] mb-12 leading-relaxed">
                ADI is committed to building a professional digital market service platform in the Asia-Pacific region, providing enterprises with comprehensive digital services from planning to implementation. Based on in-depth consulting services, combined with rich practical experience, industry expert insights, and advanced methodological tools, we provide enterprises with customized solutions and implementation services. ADI is committed to improving the success rate of enterprise projects, optimizing return on investment, and effectively managing risks.
              </p>
              <a 
                href="/en/contact-us"
                className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg w-fit whitespace-nowrap"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Accelerate Thinking Section */}
      <section className="bg-[#101214] px-4 pb-[80px] md:px-0 md:pb-[180px]">
        <div className="max-w-7xl mx-auto">
          <div className="pt-[100px] text-center md:mb-[80px]">
            <h2 className="text-[36px] font-bold leading-[54px] text-white md:text-[40px] md:leading-[60px] mb-4">
              Accelerate Thinking
            </h2>
            <p className="mx-auto max-w-[540px] text-[15px] leading-[22px] text-white/85">
              Help CXO evaluate fundamental strategic issues such as omnichannel and digital solutions
            </p>
          </div>
          
          <div className="flex flex-col justify-center text-white md:flex-row md:px-14 gap-[5%]">
            <div className="mt-[64px] flex flex-col items-center justify-start px-6 text-center md:mt-0 md:w-[30%] md:p-0">
              <img 
                alt="Activities" 
                width={88} 
                height={88} 
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/ba1fc11c2682fe53fc8766c439065671.webp" 
                className="mb-6"
              />
              <h3 className="mb-[12px] text-[22px] leading-[32px] text-white md:text-[25px] md:leading-[38px] font-bold">
                Activities
              </h3>
              <p className="text-sm text-white/85 leading-relaxed">
                Salons and forums serve as exclusive think tanks for CIO/CDO, aimed at promoting strategic dialogue on digital transformation trends and challenges in Southeast Asia to foster innovation and sustainable growth.
              </p>
            </div>
            
            <div className="mt-[64px] flex flex-col items-center justify-start px-6 text-center md:mt-0 md:w-[30%] md:p-0">
              <img 
                alt="Insights" 
                width={88} 
                height={88} 
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/9aa15216153dec80ea9994cab46aa0bd.webp" 
                className="mb-6"
              />
              <h3 className="mb-[12px] text-[22px] leading-[32px] text-white md:text-[25px] md:leading-[38px] font-bold">
                Insights
              </h3>
              <p className="text-sm text-white/85 leading-relaxed">
                Research the digitalization process in the Asia-Pacific region, including technological advances, policy changes, market trends, consumer behavior, and the impact of digitalization on the economy and society.
              </p>
            </div>
            
            <div className="mt-[64px] flex flex-col items-center justify-start px-6 text-center md:mt-0 md:w-[30%] md:p-0">
              <img 
                alt="Execution" 
                width={88} 
                height={88} 
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/5cbd8cfcfb9c492ebf61eb4618de24f5.webp" 
                className="mb-6"
              />
              <h3 className="mb-[12px] text-[22px] leading-[32px] text-white md:text-[25px] md:leading-[38px] font-bold">
                Execution
              </h3>
              <p className="text-sm text-white/85 leading-relaxed">
                Through digital consulting and execution, help organizations assess their current digital level, plan future digital strategies, and execute these strategies to achieve business goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cooperation and Support Section */}
      <section className="mb-[80px] px-5 md:mb-[140px]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-[50px] pt-[80px] text-center md:mb-[80px] md:pt-[120px]">
            <h2 className="text-[36px] font-bold leading-[54px] text-black md:text-[40px] md:leading-[60px] mb-4">
              Cooperation and Support
            </h2>
            <p className="text-[14px] leading-[24px] text-black/65">
              Through ADI consultant network, enterprises will gain international perspective and best business practices
            </p>
          </div>
          
          <div className="flex flex-wrap justify-between gap-4">
            {[
              {
                icon: "ri-bar-chart-line",
                title: "Industry Insights",
                description: "Through in-depth research and analysis of industry trends, market dynamics and technological advances, ADI helps clients gain deep understanding of the industry"
              },
              {
                icon: "ri-pie-chart-line",
                title: "Market Research",
                description: "Through continuous research and analysis, ADI has accumulated rich market knowledge and can provide professional market insights and strategies for clients"
              },
              {
                icon: "ri-team-line",
                title: "Strategic Alliance",
                description: "ADI establishes partnerships with industry leaders and decision-makers to help clients expand their influence and access key resources"
              },
              {
                icon: "ri-chat-3-line",
                title: "Dialogue Discussion",
                description: "ADI communicates closely with clients to understand their business goals and challenges, thereby providing customized solutions"
              },
              {
                icon: "ri-arrow-up-line",
                title: "Continuous Mutual Benefit",
                description: "ADI is committed to establishing long-term partnerships with clients, ensuring both parties benefit from cooperation by providing long-term value"
              },
              {
                icon: "ri-settings-3-line",
                title: "Technical Cooperation",
                description: "Supported by Flanexus technology, the institute enhances its services by leveraging Flanexus technical capabilities through cooperation"
              },
              {
                icon: "ri-tools-line",
                title: "Implementation Resources",
                description: "ADI not only provides strategic consulting but also integrates implementation resources to ensure consulting recommendations are effectively executed"
              },
              {
                icon: "ri-trophy-line",
                title: "Influence",
                description: "ADI can provide high-quality services and play an important role in clients strategic decision-making"
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="group w-[100%] md:w-[23.5%] mt-[20px] rounded-xl border border-[#A2A2A2] p-4 hover:border-[#2D66F4] hover:bg-[#2D66F4] hover:shadow-[0_4px_12px_4px_rgba(45,102,244,0.2)] transition-all duration-300"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-[#1E40AF] group-hover:bg-white rounded-full mb-4">
                  <i className={`${item.icon} text-2xl text-white group-hover:text-[#1E40AF] transition-colors duration-300`}></i>
                </div>
                <h4 className="mb-[8px] mt-[18px] text-[22px] font-bold leading-[30px] text-black group-hover:text-white transition-colors duration-300">
                  {item.title}
                </h4>
                <p className="text-[14px] leading-[20px] text-black/75 group-hover:text-white/75 transition-colors duration-300">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission and Vision Section */}
      <section className="bg-[#f8f8f8] px-5 pb-[80px] pt-[50px] md:bg-white md:px-0 md:pb-0 md:pt-0">
        <div className="max-w-7xl mx-auto">
          <div className="mb-[80px] pt-[20px] text-center">
            <h2 className="text-[32px] font-bold leading-[48px] text-black md:text-[40px] md:leading-[60px] mb-4">
              Mission and Vision
            </h2>
            <p className="text-[14px] leading-[24px] text-black/65">
              Shape the digital future of Asia-Pacific, inject new momentum for global development
            </p>
          </div>
          
          <div 
            className="relative rounded-lg bg-contain bg-cover bg-center bg-no-repeat min-h-[400px] md:min-h-[500px]"
            style={{
              backgroundImage: "url('https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/af0932ef1096903a71e0bac36d4f092b.png')"
            }}
          >
            <div className="p-10 pb-[70px] pt-[60px] md:pb-[110px] md:pl-[100px] md:pt-[100px]">
              <div className="flex flex-wrap pb-1 pt-4 md:pt-8 lg:flex-nowrap mb-8">
                <div className="mr-6 text-nowrap text-left text-[26px] font-bold leading-[42px] text-[#2D66F4] md:text-right md:text-[36px] min-w-[10%]">
                  Mission
                </div>
                <div className="text-[14px] font-normal leading-[20px] text-white md:text-[17px] md:leading-[26px] max-w-[72%] md:text-[20px] md:leading-[32px]">
                  As a digital transformation leader in Asia, especially Southeast Asia, ADI is committed to promoting sustainable and inclusive digital innovation. We provide comprehensive digital transformation guidance to enterprises through deep industry insights, cutting-edge technology research and best practice sharing, helping to build a more intelligent, efficient and sustainable digital ecosystem.
                </div>
              </div>
              
              <div className="flex flex-wrap pb-1 pt-4 md:pt-8 lg:flex-nowrap">
                <div className="mr-6 text-nowrap text-left text-[26px] font-bold leading-[42px] text-[#2D66F4] md:text-right md:text-[36px] min-w-[10%]">
                  Vision
                </div>
                <div className="text-[14px] font-normal leading-[20px] text-white md:text-[17px] md:leading-[26px] max-w-[72%] md:text-[20px] md:leading-[32px]">
                  Build an ecosystem of top chief information officers and digital leaders, providing strategic guidance to enterprises through ADI industry-leading consulting and cutting-edge technology solutions. We are committed to becoming the most influential digital transformation think tank in the Asia-Pacific region, promoting the vigorous development of regional digital economy and contributing Asian wisdom to the global digitalization process.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Framework Section */}
      <section className="px-4 py-20 pt-12 md:px-0 md:py-[140px]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-[80px] pt-[20px] text-center">
            <h2 className="text-[32px] font-bold leading-[48px] text-black md:text-[40px] md:leading-[60px] mb-16 md:mb-20">
              ADI Digital Transformation Framework
            </h2>
            
            <div className="flex flex-col-reverse md:flex-row">
              <div className="md:mr-[6%] md:min-w-80">
                <img 
                  alt="ADI Digital Framework" 
                  width={320} 
                  height={428} 
                  src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/d84b8d67d78853085d6b29988d0ab755.webp" 
                  className="w-full flex-none"
                />
              </div>
              
              <div className="flex flex-wrap gap-3 md:gap-7 md:min-w-[calc(100%-320px)]">
                {[
                  {
                    icon: "https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/f88173fafa25b98d56951a8503453d55.webp",
                    title: "Strategy & Compliance",
                    description: "Help enterprises achieve strategic goals while ensuring compliance with local regulations, strengthening data security and governance, laying the foundation for sustainable development."
                  },
                  {
                    icon: "https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/1011d9e1fa4f5584b7bdc67deb384100.webp",
                    title: "Customer & Market Insights",
                    description: "Customer-centric, supporting diverse payment methods, multilingual services and mobile optimization, helping enterprises quickly adapt to market changes."
                  },
                  {
                    icon: "https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/4eddb1d537c464fde499766199e9a00f.webp",
                    title: "Technical Capability & Scalability",
                    description: "Provide flexible, scalable technical solutions, supporting cloud deployment, disaster recovery planning and technology upgrades, ensuring efficient and stable business operations."
                  },
                  {
                    icon: "https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/6e2dd49e7085f5315eaae51334510156.webp",
                    title: "Finance & Operational Efficiency",
                    description: "Optimize core business functions, reduce cost risks, while providing training support to help enterprises achieve long-term value."
                  },
                  {
                    icon: "https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/5cbd8cfcfb9c492ebf61eb4618de24f5.webp",
                    title: "Innovation & Ecosystem Cooperation",
                    description: "Tap potential through data analysis, build an open cooperation ecosystem, and jointly promote digital innovation with enterprises."
                  }
                ].map((item, index) => (
                  <div key={index} className="flex w-[47%] flex-col md:w-[29.5%]">
                    <img 
                      alt={item.title} 
                      width={64} 
                      height={64} 
                      src={item.icon}
                      className="mb-4"
                    />
                    <h4 className="text-left font-bold leading-[22px] text-black md:leading-[32px] text-[17px] md:text-[22px] mt-[18px] mb-2">
                      {item.title}
                    </h4>
                    <p className="mb-[30px] text-left text-[13px] font-normal leading-[18px] text-black/75 md:mb-0 md:text-[15px] md:leading-[22px]">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Start Your Digital Transformation Journey
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Partner with ADI to explore the infinite possibilities of the digital future
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/en/contact-us"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold whitespace-nowrap"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="flex h-28 items-center justify-center bg-[#F7F7F7]">
        <span className="text-lg font-semibold text-black/85">Strategic Partner: </span>
        <a 
          href="https://www.flanexus.com/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="ml-1 text-lg font-semibold underline decoration-[#2D66F4] text-[#2D66F4] hover:text-blue-700"
        >
          Flanexus
        </a>
        <div className="ml-8">
          <a 
            href="https://readdy.ai/?origin=logo" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
};

export default HomeEn;
